import React, { useState } from "react";
import { Box, Button, IconButton, List, ListItem, ListItemButton, ListItemText, TextField, Typography } from "@mui/material";
import { Delete, Add, Edit, UploadFile } from "@mui/icons-material";
import { ChatMessageModel, ClarificationResponse } from "../../client/SmartAssistantBackendClient";
import type { AgentSelectionModel } from "../../contexts/CurrentThreadContext";

export type ChatSessionViewModel = {
  id: string;
  title: string;
  manualTitle?: string;
  updatedAt: number;
  messages: ChatMessageModel[];
  clarification?: ClarificationResponse;
  agentSelection?: AgentSelectionModel;
};

type Props = {
  sessions: ChatSessionViewModel[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, newTitle: string) => void;
  onNew: () => void;
  onImport: () => void;
  disabled?: boolean;
};

export const ChatSessionsPanel: React.FC<Props> = ({ sessions, selectedId, onSelect, onDelete, onRename, onNew, onImport, disabled = false }) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const handleStartEdit = (id: string, currentTitle: string) => {
    setEditingId(id);
    setEditValue(currentTitle);
  };

  const handleCommitEdit = () => {
    if (editingId) {
      const trimmed = editValue.trim();
      if (trimmed) onRename(editingId, trimmed);
    }
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 2,
        background: theme.palette.background.paper,
        minHeight: 0,
      })}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>Chat-Sessions</Typography>
          <Typography variant="caption" color="text.secondary">Lokal gespeichert</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button startIcon={<UploadFile />} variant="outlined" size="small" onClick={onImport} disabled={disabled}>
            Import
          </Button>
          <Button startIcon={<Add />} variant="outlined" size="small" onClick={onNew} disabled={disabled}>
            Neu
          </Button>
        </Box>
      </Box>

      <List dense sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
        {sessions.length === 0 && (
          <ListItem disablePadding>
            <ListItemText primary="Noch keine Sessions." primaryTypographyProps={{ color: 'text.secondary' }} />
          </ListItem>
        )}
        {sessions.map((t) => {
          const isEditing = editingId === t.id;
          return (
            <ListItem
              disablePadding
              key={t.id}
              secondaryAction={
                !isEditing ? (
                  <Box display="flex" gap={0.5}>
                    <IconButton edge="end" size="small" onClick={() => handleStartEdit(t.id, t.title)} disabled={disabled} title="Umbenennen">
                      <Edit fontSize="small" />
                    </IconButton>
                    {t.messages?.length > 0 && (
                      <IconButton edge="end" size="small" onClick={() => onDelete(t.id)} disabled={disabled} title="Löschen">
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ) : null
              }
            >
              {isEditing ? (
                <Box sx={{ flex: 1, py: 0.5, px: 1 }}>
                  <TextField
                    autoFocus
                    size="small"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={handleCommitEdit}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); handleCommitEdit(); }
                      if (e.key === 'Escape') { e.preventDefault(); handleCancelEdit(); }
                    }}
                    fullWidth
                    inputProps={{ maxLength: 100 }}
                    variant="outlined"
                  />
                </Box>
              ) : (
                <ListItemButton
                  selected={selectedId === t.id}
                  onClick={() => onSelect(t.id)}
                  disabled={disabled}
                  sx={(theme) => ({
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                    backgroundColor: selectedId === t.id
                      ? theme.palette.action.selected
                      : theme.palette.action.hover,
                    '&:hover': { borderColor: theme.palette.text.secondary },
                  })}
                >
                  <ListItemText
                    primary={t.title || 'Ohne Titel'}
                    secondary={`${t.messages?.length ?? 0} Nachrichten`}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                    secondaryTypographyProps={{ color: 'text.secondary' }}
                  />
                </ListItemButton>
              )}
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};
